import { useEffect, useState } from 'react';
import {
  ChevronDown,
  ExternalLink,
  X,
} from 'lucide-react';
import type { OnboardedData } from '@/components/smartai/api/getComponentList';
import {
  type CoverageSummary,
  type CoverageByCodeElement,
  type LinesCoverageFile,
  type InventoryCoverageFile,
  type EntityDetails,
  type EntityDetailsInventory,
  useIgnoreEntity,
  type IgnoreEntityRequest,
} from '@/components/smartai/api/getCoverageAggergate';
import {
  useFileContent,
  type FileContentCoverageBucket,
} from '@/components/smartai/api/getFileContent';
import CodeEditor from '@/components/common/CodeEditor';
import { Button } from '@/components/ui/button';
import { CoverageCodeModalSkeleton } from './CoverageDashboardSkeleton';
import { OnboardingTable } from '@/components/ui/onboarding-table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  CoverageMetricCard,
  coverageCardClass,
  coverageElementGridClass,
  coverageMetricsGridClass,
  coveragePctClass,
  type CoverageMode,
} from './coverage-dashboard-layout';

export type SummaryVariant = 'combined' | 'automation' | 'manual';

export type FileCoverageFilter = 'all' | 'covered' | 'uncovered';

const FILE_COVERAGE_FILTER_OPTIONS: { value: FileCoverageFilter; label: string }[] =
  [
    { value: 'all', label: 'All' },
    { value: 'covered', label: 'Covered' },
    { value: 'uncovered', label: 'Uncovered' },
  ];

export function filterLinesFiles(
  files: LinesCoverageFile[],
  filter: FileCoverageFilter,
): LinesCoverageFile[] {
  if (filter === 'covered') {
    return files.filter(f => f.coveredLinesCount > 0);
  }
  if (filter === 'uncovered') {
    // Fully uncovered only (partially covered files belong under Covered / All).
    return files.filter(f => f.coveredLinesCount === 0);
  }
  return files;
}

export function filterInventoryFiles(
  files: InventoryCoverageFile[],
  filter: FileCoverageFilter,
): InventoryCoverageFile[] {
  if (filter === 'covered') {
    return files.filter(f => f.coveredElements > 0);
  }
  if (filter === 'uncovered') {
    return files.filter(f => f.coveredElements === 0);
  }
  return files;
}

export function useAutoLoadCoveragePages(
  hasMore: boolean,
  isFetching: boolean,
  isPlaceholderData: boolean,
  onLoadMore: () => void,
) {
  useEffect(() => {
    if (hasMore && !isFetching && !isPlaceholderData) {
      onLoadMore();
    }
  }, [hasMore, isFetching, isPlaceholderData, onLoadMore]);
}

export function FileCoverageFilterBar({
  value,
  onChange,
  shown,
  total,
  isLoading,
}: {
  value: FileCoverageFilter;
  onChange: (value: FileCoverageFilter) => void;
  shown: number;
  total: number;
  isLoading?: boolean;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 flex-wrap items-center gap-2.5">
        <span className="text-[0.8125rem] font-medium text-[var(--inv-text-secondary)]">
          Show files
        </span>
        <div
          className="inline-flex rounded-[10px] border border-solid border-[var(--inv-border-soft)] bg-[var(--inv-surface-muted)] p-0.5 shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))]"
          role="group"
          aria-label="Show files">
          {FILE_COVERAGE_FILTER_OPTIONS.map(opt => {
            const active = opt.value === value;
            return (
              <Button
                key={opt.value}
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  'h-auto rounded-lg px-3 py-1.5 text-[0.8125rem] font-medium shadow-none hover:!translate-y-0 hover:!bg-[var(--inv-surface)]',
                  active
                    ? 'bg-[var(--inv-surface)] text-[var(--inv-accent)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.06))] hover:!text-[var(--inv-accent)]'
                    : 'bg-transparent text-[var(--inv-text-secondary)] hover:!text-[var(--inv-text)]',
                )}
                aria-pressed={active}
                onClick={() => onChange(opt.value)}>
                {opt.label}
              </Button>
            );
          })}
        </div>
      </div>
      <span className="text-[0.8125rem] tabular-nums text-[var(--inv-text-muted)]">
        {isLoading
          ? `Loading files… (${shown} of ${total})`
          : `Showing ${shown} of ${total} files`}
      </span>
    </div>
  );
}

type EntityRow = EntityDetails | EntityDetailsInventory;

interface ViewCodeModalProps {
  open: boolean;
  filePath: string;
  selected: OnboardedData;
  onClose: () => void;
  coverageBucket?: FileContentCoverageBucket;
  entities?: EntityRow[];
  showLocCoverage?: boolean;
  scanId?: string;
  onIgnoreSuccess?: () => void;
}

function useEntityIgnoreState(
  entities: EntityRow[],
  scanId: string,
  onIgnoreSuccess?: () => void,
) {
  const { mutate: ignoreFn, isPending: isIgnoring } = useIgnoreEntity();
  const [localEntities, setLocalEntities] = useState<EntityRow[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setLocalEntities([...entities].sort(compareByEntityType));
  }, [entities]);

  const handleIgnoreEntity = (entity: EntityRow) => {
    setErrorMessage(null);
    const ignoreReqObj: IgnoreEntityRequest = {
      scanId,
      entities: [
        {
          entityId: entity.entityId,
          isIgnored: !entity.is_ignored,
        },
      ],
      cascadeChildren: true,
    };
    setLocalEntities(prev =>
      prev.map(e =>
        e.entityId === entity.entityId
          ? { ...e, is_ignored: !e.is_ignored }
          : e,
      ),
    );
    ignoreFn(ignoreReqObj, {
      onSuccess: () => onIgnoreSuccess?.(),
      onError: (error: Error) => {
        setLocalEntities(prev =>
          prev.map(e =>
            e.entityId === entity.entityId
              ? { ...e, is_ignored: entity.is_ignored }
              : e,
          ),
        );
        setErrorMessage(
          `Failed to update "${entity.entityName}": ${error.message}`,
        );
      },
    });
  };

  return { localEntities, handleIgnoreEntity, isIgnoring, errorMessage };
}

function entityCoverageLabel(row: EntityRow, showLocCoverage: boolean): string {
  if (showLocCoverage && 'locCoverage' in row) {
    return `${(row.locCoverage?.totalCoverage ?? 0).toFixed(1)}% LOC`;
  }
  return `${(row.inventoryCoverage?.totalCoverage ?? 0).toFixed(1)}% inv`;
}

function ViewCodeEntitySidebar({
  entities,
  scanId,
  showLocCoverage,
  onIgnoreSuccess,
}: {
  entities: EntityRow[];
  scanId: string;
  showLocCoverage: boolean;
  onIgnoreSuccess?: () => void;
}) {
  const { localEntities, handleIgnoreEntity, isIgnoring, errorMessage } =
    useEntityIgnoreState(entities, scanId, onIgnoreSuccess);

  return (
    <aside
      className="flex max-h-[min(40vh,22rem)] w-full shrink-0 flex-col overflow-hidden rounded-lg border border-solid border-[var(--inv-border)] bg-[var(--inv-surface-muted)] p-2 sm:max-h-none sm:w-72 sm:rounded-none sm:border-0 sm:border-l sm:bg-transparent sm:p-0 sm:pl-3"
      aria-label="File entities">
      <h4 className="m-0 px-1 pb-2 text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-[var(--inv-text-muted)]">
        Entities
      </h4>
      {errorMessage ? (
        <p className="mb-2 rounded-md border border-solid border-[#fecaca] bg-[#fef2f2] px-2 py-1.5 text-xs text-[#b42318]">
          {errorMessage}
        </p>
      ) : null}
      {localEntities.length === 0 ? (
        <p className="m-0 px-1 text-xs text-[var(--inv-text-secondary)]">
          No entities for this file.
        </p>
      ) : (
        <ul className="m-0 min-h-0 flex-1 list-none overflow-y-auto p-0 pr-1">
          {localEntities.map(row => (
            <li
              key={row.entityId}
              className="border-0 border-b border-solid border-[var(--inv-border-soft)] py-2.5 last:border-b-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p
                    className="m-0 truncate text-[0.8125rem] font-medium text-[var(--inv-text)]"
                    title={row.entityName}>
                    {row.entityName}
                  </p>
                  <p className="m-0 mt-0.5 text-[0.6875rem] text-[var(--inv-text-muted)]">
                    {row.entityType} · {row.startLine}–{row.endLine}
                  </p>
                  <p className="m-0 mt-0.5 tabular-nums text-[0.6875rem] text-[var(--inv-text-secondary)]">
                    {entityCoverageLabel(row, showLocCoverage)}
                  </p>
                </div>
                <IgnoreToggle
                  checked={row.is_ignored}
                  disabled={isIgnoring}
                  onToggle={() => handleIgnoreEntity(row)}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

export function ViewCodeModal({
  open,
  filePath,
  selected,
  onClose,
  coverageBucket = 'combined',
  entities = [],
  showLocCoverage = true,
  scanId,
  onIgnoreSuccess,
}: ViewCodeModalProps) {
  const { data, isPending, isError, refetch } = useFileContent({
    repoUrl: selected.repoUrl,
    branchName: selected.branchName,
    filePath,
    scanId: selected.scanId,
    pat: selected.pat,
    coverageBucket,
  });

  const coveredLines =
    data?.data?.coverage?.linesOfCodeMode?.coveredLines ?? [];
  const executableLines = data?.data?.lineClassification?.executable ?? [];
  const uncoveredLines = executableLines.filter(l => !coveredLines.includes(l));
  const showEntitySidebar = Boolean(scanId);

  const handleIgnoreSuccess = () => {
    onIgnoreSuccess?.();
    void refetch();
  };

  return (
    <Dialog open={open} onOpenChange={val => !val && onClose()}>
      <DialogContent className="flex max-h-[90vh] w-[92vw] max-w-[min(92vw,88rem)] flex-col overflow-hidden p-4 sm:p-5">
        <DialogHeader className="shrink-0">
          <DialogTitle className="break-all pr-8">{filePath}</DialogTitle>
        </DialogHeader>
        {isPending ? <CoverageCodeModalSkeleton /> : null}
        {isError ? (
          <p className="shrink-0 px-1 py-4 text-sm text-[#b42318]">
            Failed to load file content.
          </p>
        ) : null}
        {data?.data ? (
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden sm:flex-row sm:gap-4">
            <div className="min-h-0 min-w-0 flex-1 overflow-auto">
              <CodeEditor
                codeFromApi={data.data.content}
                language={data.data.language ?? filePath}
                executable={executableLines}
                nonExecutable={data.data.lineClassification?.nonExecutable ?? []}
                covered={coveredLines}
                uncovered={uncoveredLines}
                instanceId={filePath}
              />
            </div>
            {showEntitySidebar ? (
              <ViewCodeEntitySidebar
                entities={entities}
                scanId={scanId!}
                showLocCoverage={showLocCoverage}
                onIgnoreSuccess={handleIgnoreSuccess}
              />
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface SummaryCardsProps {
  summary: CoverageSummary;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ui_coverage: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  api_coverage: any;
  variant: SummaryVariant;
}

export function SummaryCards({
  summary,
  ui_coverage,
  api_coverage,
  variant,
}: SummaryCardsProps) {
  const cards = [
    <CoverageMetricCard
      key="total"
      title="Total coverage"
      value={`${(summary?.totalCoverage?.percentage || 0).toFixed(1)}%`}
      subtitle={`${summary?.totalCoverage?.covered || 0} / ${summary?.totalCoverage?.total || 0} covered`}
      progress={summary?.totalCoverage?.percentage || 0}
      accent="default"
    />,
    variant !== 'manual' ? (
      <CoverageMetricCard
        key="auto"
        title="Test automation"
        value={`${(summary?.testAutomation?.percentage || 0).toFixed(1)}%`}
        subtitle={`${summary?.testAutomation?.covered || 0} / ${summary?.testAutomation?.total || 0} covered`}
        progress={summary?.testAutomation?.percentage || 0}
        accent="success"
      />
    ) : null,
    variant !== 'automation' ? (
      <CoverageMetricCard
        key="manual"
        title="Manual tests"
        value={`${(summary?.manualTests?.percentage || 0).toFixed(1)}%`}
        subtitle={`${summary?.manualTests?.covered || 0} / ${summary?.manualTests?.total || 0} covered`}
        progress={summary?.manualTests?.percentage || 0}
        accent="info"
      />
    ) : null,
    <CoverageMetricCard
      key="uncovered"
      title="Uncovered"
      value={`${(summary?.uncovered?.percentage || 0).toFixed(1)}%`}
      subtitle={`${summary?.uncovered?.uncovered || 0} / ${summary?.uncovered?.total || 0} uncovered`}
      progress={summary?.uncovered?.percentage || 0}
      accent="danger"
    />,
    variant === 'combined' ? (
      <CoverageMetricCard
        key="ui"
        title="UI coverage"
        value={
          Object.keys(ui_coverage).length > 0
            ? `${ui_coverage?.totalCoverage?.percentage}%`
            : 'N/A'
        }
      />
    ) : null,
    variant === 'combined' ? (
      <CoverageMetricCard
        key="api"
        title="API coverage"
        value={
          Object.keys(api_coverage).length > 0
            ? `${api_coverage?.totalCoverage?.percentage}%`
            : 'N/A'
        }
      />
    ) : null,
  ].filter(Boolean);

  return (
    <div className={coverageMetricsGridClass}>
      {cards}
    </div>
  );
}

const ELEMENT_LABELS: Array<{
  key: keyof CoverageByCodeElement;
  label: string;
}> = [
  { key: 'classes', label: 'Classes' },
  { key: 'methods', label: 'Methods' },
  { key: 'branches', label: 'Branches' },
  { key: 'functions', label: 'Functions' },
  { key: 'loops', label: 'Loops' },
];

export function ElementBreakdown({
  elements,
}: {
  elements: CoverageByCodeElement;
}) {
  return (
    <div className={coverageElementGridClass}>
      {ELEMENT_LABELS.map(({ key, label }) => {
        const el = elements[key];
        return (
          <CoverageMetricCard
            key={key}
            title={label}
            value={`${(el?.percentage || 0).toFixed(1)}%`}
            subtitle={`${el?.covered || 0} / ${el?.total || 0}`}
            progress={el?.percentage || 0}
          />
        );
      })}
    </div>
  );
}

function TableActionButtons({
  onViewCode,
  onViewDetails,
}: {
  onViewCode: () => void;
  onViewDetails: () => void;
}) {
  return (
    <div className="flex items-center justify-end gap-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        aria-label="View code"
        onClick={onViewCode}>
        <ExternalLink className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        aria-label="View details"
        onClick={onViewDetails}>
        <ChevronDown className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function LinesTable({
  files,
  onViewCode,
  onViewDetails,
}: {
  files: LinesCoverageFile[];
  onViewCode: (filePath: string) => void;
  onViewDetails: (filePath: string) => void;
}) {
  return (
    <div className="mb-5">
      <h3 className="mb-3 font-serif text-[0.9375rem] font-semibold text-[var(--inv-text)]">
        File-level coverage
      </h3>
      <OnboardingTable
        data={files}
        rowKey={row => row.filePath}
        columns={[
          {
            key: 'filePath',
            header: 'File',
            render: row => (
              <span className="break-all font-mono text-[0.8125rem] text-[var(--inv-accent)]">
                {row.filePath}
              </span>
            ),
          },
          {
            key: 'totalCoverage',
            header: 'Coverage %',
            render: row => (
              <span className={coveragePctClass(row.totalCoverage)}>
                {row.totalCoverage.toFixed(1)}%
              </span>
            ),
          },
          {
            key: 'totalExecutableLines',
            header: 'Exec. lines',
            render: row => (
              <span className="tabular-nums">{row.totalExecutableLines}</span>
            ),
          },
          {
            key: 'coveredLinesCount',
            header: 'Covered',
            render: row => (
              <span className="tabular-nums text-[#16a34a]">
                {row.coveredLinesCount}
              </span>
            ),
          },
          {
            key: 'uncoveredLines',
            header: 'Uncovered',
            render: row => (
              <span className="tabular-nums text-[#d92d20]">
                {row.uncoveredLines}
              </span>
            ),
          },
          {
            key: 'actions',
            header: '',
            className: 'w-24 text-right',
            render: row => (
              <TableActionButtons
                onViewCode={() => onViewCode(row.filePath)}
                onViewDetails={() => onViewDetails(row.filePath)}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

export function InventoryTable({
  files,
  onViewCode,
  onViewDetails,
}: {
  files: InventoryCoverageFile[];
  onViewCode: (filePath: string) => void;
  onViewDetails: (filePath: string) => void;
}) {
  const fraction = (covered: number, total: number) => (
    <span className="tabular-nums">
      {covered}/{total}
    </span>
  );

  return (
    <div className="mb-5">
      <h3 className="mb-3 font-serif text-[0.9375rem] font-semibold text-[var(--inv-text)]">
        File-level inventory coverage
      </h3>
      <OnboardingTable
        data={files}
        rowKey={row => row.filePath}
        columns={[
          {
            key: 'filePath',
            header: 'File',
            render: row => (
              <span className="break-all font-mono text-[0.8125rem] text-[var(--inv-accent)]">
                {row.filePath}
              </span>
            ),
          },
          {
            key: 'totalCoverage',
            header: 'Coverage %',
            render: row => (
              <span className={coveragePctClass(row.totalCoverage)}>
                {row.totalCoverage.toFixed(1)}%
              </span>
            ),
          },
          {
            key: 'classes',
            header: 'Classes',
            render: row => fraction(row.classes.covered, row.classes.total),
          },
          {
            key: 'methods',
            header: 'Methods',
            render: row => fraction(row.methods.covered, row.methods.total),
          },
          {
            key: 'branches',
            header: 'Branches',
            render: row => fraction(row.branches.covered, row.branches.total),
          },
          {
            key: 'functions',
            header: 'Functions',
            render: row => fraction(row.functions.covered, row.functions.total),
          },
          {
            key: 'loops',
            header: 'Loops',
            render: row => fraction(row.loops.covered, row.loops.total),
          },
          {
            key: 'coveredBy',
            header: 'Auto / manual',
            render: row => (
              <span className="tabular-nums">
                {row.coveredBy.auto.toFixed(0)}% /{' '}
                {row.coveredBy.manual.toFixed(0)}%
              </span>
            ),
          },
          {
            key: 'actions',
            header: '',
            className: 'w-24 text-right',
            render: row => (
              <TableActionButtons
                onViewCode={() => onViewCode(row.filePath)}
                onViewDetails={() => onViewDetails(row.filePath)}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

function compareByEntityType(a: EntityRow, b: EntityRow) {
  if (a.entityType === 'Method' && b.entityType !== 'Method') return -1;
  if (a.entityType !== 'Method' && b.entityType === 'Method') return 1;
  return 0;
}

function IgnoreToggle({
  checked,
  disabled,
  onToggle,
}: {
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={cn(
        'relative h-5 w-9 shrink-0 rounded-full border-0 p-0 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring,rgba(0,70,184,0.14))] focus-visible:ring-offset-1 focus-visible:ring-offset-[var(--inv-surface)]',
        'disabled:cursor-not-allowed disabled:opacity-50',
        checked
          ? 'cursor-pointer bg-[var(--inv-accent)] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] hover:bg-[var(--inv-accent-hover,#00378f)]'
          : 'cursor-pointer bg-[var(--inv-border-strong,#d4c4b0)] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)] hover:bg-[#c4b5a6]',
      )}
      onClick={onToggle}>
      <span
        className={cn(
          'pointer-events-none absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.18)] transition-transform',
          checked && 'translate-x-4',
        )}
      />
    </button>
  );
}

export function EntityDetailsPanel({
  filePath,
  entities,
  showLocCoverage,
  onClose,
  scanId,
  onIgnoreSuccess,
}: {
  filePath: string;
  entities: EntityRow[];
  showLocCoverage: boolean;
  onClose: () => void;
  scanId: string;
  onIgnoreSuccess: () => void;
}) {
  const { localEntities, handleIgnoreEntity, isIgnoring, errorMessage } =
    useEntityIgnoreState(entities, scanId, onIgnoreSuccess);

  return (
    <div
      className={cn(
        coverageCardClass,
        'mt-5 border-t-4 border-t-[var(--inv-accent-soft)]',
      )}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="m-0 break-all font-serif text-[0.9375rem] font-semibold text-[var(--inv-text)]">
          Entity details — {filePath}
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 shrink-0 p-0"
          aria-label="Close entity details"
          onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {errorMessage ? (
        <p className="mb-4 rounded-lg border border-solid border-[#fecaca] bg-[#fef2f2] px-3 py-2 text-sm text-[#b42318]">
          {errorMessage}
        </p>
      ) : null}

      {localEntities.length === 0 ? (
        <p className="text-sm text-[var(--inv-text-secondary)]">
          No entity details available for this file.
        </p>
      ) : (
        <OnboardingTable
          data={localEntities}
          rowKey={row => row.entityId}
          columns={[
            {
              key: 'entityName',
              header: 'Entity',
              render: row => row.entityName,
            },
            {
              key: 'entityType',
              header: 'Type',
              render: row => row.entityType,
            },
            showLocCoverage
              ? {
                  key: 'locCoveragePct',
                  header: 'LOC coverage %',
                  render: (row: EntityRow) => (
                    <span className="tabular-nums">
                      {(
                        ('locCoverage' in row
                          ? row.locCoverage?.totalCoverage
                          : 0) ?? 0
                      ).toFixed(1)}
                      %
                    </span>
                  ),
                }
              : {
                  key: 'inventoryCoveragePct',
                  header: 'Inventory coverage %',
                  render: (row: EntityRow) => (
                    <span className="tabular-nums">
                      {(row.inventoryCoverage?.totalCoverage ?? 0).toFixed(1)}%
                    </span>
                  ),
                },
            {
              key: 'startLine',
              header: 'Start line',
              render: (row: EntityRow) => (
                <span className="tabular-nums">{row.startLine}</span>
              ),
            },
            {
              key: 'endLine',
              header: 'End line',
              render: (row: EntityRow) => (
                <span className="tabular-nums">{row.endLine}</span>
              ),
            },
            showLocCoverage
              ? {
                  key: 'execLines',
                  header: 'Exec lines',
                  render: (row: EntityRow) => (
                    <span className="tabular-nums">
                      {(
                        ('locCoverage' in row
                          ? row.locCoverage?.totalExecutableLinesCount
                          : 0) ?? 0
                      ).toFixed(0)}
                    </span>
                  ),
                }
              : {
                  key: 'childrenTotal',
                  header: 'Total children',
                  render: (row: EntityRow) => (
                    <span className="tabular-nums">
                      {(row.inventoryCoverage?.childrenTotal ?? 0).toFixed(0)}
                    </span>
                  ),
                },
            showLocCoverage
              ? {
                  key: 'covered',
                  header: 'Covered',
                  render: (row: EntityRow) => (
                    <span className="tabular-nums text-[#16a34a]">
                      {(
                        ('locCoverage' in row
                          ? row.locCoverage?.coveredLinesCount
                          : 0) ?? 0
                      ).toFixed(0)}
                    </span>
                  ),
                }
              : {
                  key: 'coveredChildren',
                  header: 'Covered',
                  render: (row: EntityRow) => (
                    <span className="tabular-nums text-[#16a34a]">
                      {(row.inventoryCoverage?.childrenCovered ?? 0).toFixed(0)}
                    </span>
                  ),
                },
            showLocCoverage
              ? {
                  key: 'uncovered',
                  header: 'Uncovered',
                  render: (row: EntityRow) => (
                    <span className="tabular-nums text-[#d92d20]">
                      {(
                        ('locCoverage' in row
                          ? row.locCoverage?.uncoveredLinesCount
                          : 0) ?? 0
                      ).toFixed(0)}
                    </span>
                  ),
                }
              : {
                  key: 'uncoveredChildren',
                  header: 'Uncovered',
                  render: (row: EntityRow) => (
                    <span className="tabular-nums text-[#d92d20]">
                      {(
                        (row.inventoryCoverage?.childrenTotal ?? 0) -
                        (row.inventoryCoverage?.childrenCovered ?? 0)
                      ).toFixed(0)}
                    </span>
                  ),
                },
            {
              key: 'ignore',
              header: 'Ignore',
              className: 'w-20',
              render: (row: EntityRow) => (
                <IgnoreToggle
                  checked={row.is_ignored}
                  disabled={isIgnoring}
                  onToggle={() => handleIgnoreEntity(row)}
                />
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

export type { CoverageMode };
